import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { ProductTypeProperty } from '@deltek/specification-client/core/Models/productTypeProperty'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    reactiveWorkspaceContents,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import {
    BulkAddElementInput,
    BulkAddElementInputWithMetadata,
} from 'models/getProjectElementNodesInput'
import {
    SetProjectContentItemElementsInput,
    TextContentElementInput,
} from 'models/setProjectContentItemElementsInput'
import {
    CheckContentCauseKeyDuplicatesRequirements,
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
} from './model'
import {
    findInTemplates,
    findItemInTemplates,
    getContentOwnerId,
} from './utils'

export const forbiddenPropertyCharacters = [
    ':',
    '<',
    '>',
    '[',
    ']',
    '|',
    '{',
    '}',
    '&lt;',
    '&gt;',
]
export const articleDefaultValue = `Insert Article Name`
export const AddArticleErrorMessage = `The article name is not unique or contains the following - <> {} [] :. \nPress the Escape (Esc) key to cancel this action.`
export const AddPropertyErrorMessage = `The key is not unique or contains the following - <> {} []. Press the Escape (Esc) key to cancel this action.`
export const KeyErrorMessage =
    'The key is not unique or contains the following - <> {} []. Please refresh the page before moving to a different property.'
export const ProductGroupKeyErrorMessage =
    'Create a key to save your entry. A key consists of a text value followed by a colon.'
export const KeyGenerationSuccessMessage =
    'We Auto-Generated an Intelligent Key For You!'
export const EditArticleErrorMessage = `The article name is not unique or contains the following - <> {} [] :. Please refresh the page before moving to a different article.`
export const PromoteDemoteBaseContentError =
    'Cannot promote or demote MasterSpec content. Create custom content for added flexibility.'
export const PromoteDemoteGeneralError =
    'There was a problem promoting/demoting the content'
export const contentMinLevel = 1
export const contentMaxLevel = 6

export const DuplicateKeyErrorMessage =
    'The action could not be saved. A duplicate key (text before the colon) cannot exist on the same level under a parent.'

export const getPropertyDefaultValue = () => {
    return processEnv.REACT_APP_EnablePasteFromWord === '1'
        ? 'Type your value here, or, paste content.'
        : 'Add your value here.'
}

export const defaultNewProperty: Partial<NormalizedChildDocumentTemplateViewItem> = {
    contentItem: {
        id: 'temporaryContenItemId',
        elements: [
            {
                id: 'temporaryContentItemId',
                type: 'NewlyAdded',
            },
        ],
    },
    id: 'temporaryTemplateId',
    addingState: 'newlyAdded',
    isUserAdded: true,
}

export const validateAddInput = (
    propertyKey: string,
    isForArticle: boolean,
    siblingContents?: Partial<ContentItem>[]
) => {
    return checkKeyDuplicates(propertyKey ?? '', isForArticle, siblingContents)
}
export const getNewlyAddedProperties = (
    parent: Partial<NormalizedDocumentTemplateViewItem>,
    isPropertyShared: boolean,
    triggerElementParent?: Partial<NormalizedDocumentTemplateViewItem>
) => {
    const isNestedContentItem = parent?.type === 'ContentItem'
    const newIndex =
        parent?.children?.length! > 0
            ? parent?.children?.findIndex(
                  (sibling) => sibling.id === triggerElementParent?.id
              )! + 1
            : 0
    const isActuallyPTP =
        isNestedContentItem &&
        parent?.propertyRefId &&
        parent?.propertyRefId !== ''
    const newOrderIndex =
        parent?.contentItem?.isProductGroup && triggerElementParent?.order! >= 0
            ? triggerElementParent?.order! + 1
            : newIndex

    return {
        contentItemOwnerType: isNestedContentItem
            ? !isActuallyPTP
                ? parent?.parentType
                : 'ProductTypeProperty'
            : parent?.type ?? 'Nugget',
        contentItemParentId: triggerElementParent?.parentId ?? parent?.id,
        contentItemParentType:
            triggerElementParent?.type ?? parent?.type ?? 'Nugget',
        index: newOrderIndex,
        uiIndex: newIndex,
        partType: parent?.partType,
        textType: 'Paragraph',
        productTypePropertyType: isPropertyShared ? 'Shared' : 'Specific',
    }
}

//edit
export declare type GetPropertyEditingDetailsProps = {
    property: NormalizedDocumentTemplateViewItem
    isForArticle: boolean
    nodeId: string
    contentItemParentId: string
    parentType: string
    parentKey?: string
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    projectId?: string
}
export const getPropertyEditingDetails = ({
    property,
    isForArticle,
    nodeId,
    contentItemParentId,
    parentType,
    parentKey,
    selectedTopic,
    projectId,
}: GetPropertyEditingDetailsProps): SetProjectContentItemElementsInput => {
    return {
        projectProductFamilyId: !!selectedTopic
            ? selectedTopic?.ppfId ?? ''
            : (workspaceContents().projectProductFamilyId as string),
        contentItemId: property?.contentItem?.id ?? '',
        contentItemOwnerType: property?.propertyRefId
            ? 'ProductTypeProperty'
            : property?.parentType ?? '',
        elements: property?.contentItem?.elements?.map(
            (element) =>
                extractElement(element) as Partial<TextContentElementInput>
        ) as Partial<TextContentElementInput>[],
        projectId: !!selectedTopic
            ? 'DraftMasterSpecProject'
            : projectId ?? property?.documentTemplateView?.projectId ?? '',
        contentItemOwnerId: getContentOwnerId(property) ?? '',
        nodeId,
        textType: isForArticle ? 'Article' : 'Paragraph',
        partType: !!selectedTopic
            ? 'CONTENTGROUP'
            : (property?.partType as string),
        documentTemplateViewId: property?.documentTemplateView?.id as string,
        documentTemplateViewItemId: property?.documentTemplateViewItem
            ?.itemId as string,
        contentItemParentId: contentItemParentId ?? '',
        contentItemParentType: isForArticle ? parentType : 'ContentItem',
        propertyKey: !isForArticle
            ? (extractKey(
                  property,
                  property?.parentId as string,
                  undefined,
                  parentKey
              ) as string)
            : '',
    }
}
//KEY RELATED
export const hasDuplicateKey = (
    actualKey: string,
    ptps: Partial<ProductTypeProperty>[] | undefined,
    siblingContents?: Partial<ContentItem>[]
) => {
    const trimmedKey = actualKey?.substring(actualKey?.lastIndexOf('|') + 1)

    return !!(
        ptps?.find(
            (properties) =>
                properties.name?.toLocaleLowerCase().trim() ===
                actualKey.toLocaleLowerCase().trim()
        ) ||
        siblingContents?.find((sib) => {
            let key = ''
            let i = 0
            // looping throught the elements to get the whole key
            // When we insert a new text between the keys, it breaks into multiple elements
            // So we loop through the elements and find the exact key
            do {
                const currentElement = sib?.elements?.[i]
                if (
                    !currentElement ||
                    currentElement?.text?.substring(0, 1) === ':'
                ) {
                    break
                }
                key +=
                    currentElement?.text?.indexOf(':') !== -1
                        ? currentElement?.text?.substring(
                              0,
                              currentElement?.text?.indexOf(':')
                          )
                        : currentElement?.text
                i++
            } while (true)

            return (
                key?.toLocaleLowerCase().trim() ===
                trimmedKey.toLocaleLowerCase().trim()
            )
        })
    )
}

export const articleHasDuplicateKey = (
    actualKey: string,
    partType?: string,
    updatedElementId?: string
) => {
    const { contentItemNodes, dtv } = reactiveWorkspaceContents()
    const articles = contentItemNodes?.filter((content) => {
        const dtvContent = dtv?.items?.find((dtvItem) =>
            dtvItem?.documentTemplateViewContents?.find((dtvContent) =>
                dtvContent.documentTemplateViewContentItems?.find(
                    (contentItem) => contentItem?.id === content.id
                )
            )
        )

        return (
            !!dtvContent && // checking if the dtv exists
            (content.textType === 'Article' || content?.textType === 'Part') &&
            dtvContent?.partType === partType
        )
    })

    return !!articles?.find((article) => {
        const value = article.elements
            ?.map((element) =>
                // mapping through the elements in one article element to get updated text value
                element?.changeType?.toUpperCase() === 'DELETED' ||
                updatedElementId === element.id // skipping current edited element
                    ? ''
                    : element.text
            )
            .join('')
        return value?.toLowerCase().trim() === actualKey.toLowerCase().trim()
    })
}

export const checkKeyDuplicates = (
    key: string,
    isForArticle: boolean,
    siblingContents?: Partial<ContentItem>[]
) => {
    const productTypeProperties = workspaceContents()?.productTypeProperties
    if (!key || key === '') return false
    else if (
        !isForArticle &&
        (!siblingContents || siblingContents?.length === 0)
    )
        return false
    else
        return isForArticle
            ? articleHasDuplicateKey(key)
            : hasDuplicateKey(
                  key.substring(key.indexOf('|') + 1),
                  productTypeProperties,
                  siblingContents
              )
}

export const validateProductGroupKey = (
    isProductGroup: boolean,
    propertyKey: string
) => {
    return isProductGroup && propertyKey === ''
}

export const extractKey = (
    propertyToMove: NormalizedDocumentTemplateViewItem,
    newParentId: string,
    updatedItems?: NormalizedDocumentTemplateViewItem[],
    parentKey?: string
) => {
    // Changed this block to get the correct PTP key when the keys is edited
    const combinedTexts = propertyToMove?.contentItem?.elements
        ?.map((element: Partial<TextContentElement>) => {
            if (element?.changeType?.toUpperCase() !== 'DELETED') {
                return element?.text
            }
            return ''
        })
        ?.join('')

    const propertyWithKey = combinedTexts?.slice(0, combinedTexts.indexOf(':'))

    const newParent = parentKey
        ? ({ parentKey } as NormalizedDocumentTemplateViewItem)
        : findItemInTemplates(updatedItems!, newParentId)

    // Previously the key was sliced from '('. Not sure why it was implemented
    // eg: 'Full-Height (Continuous) Type' key will be sent as 'Full-Height ' to api which is wrong. so removed the condition
    return propertyWithKey
        ? (`${
              newParent?.parentKey ?? '|'
          }|${propertyWithKey}` as string)?.replace(/\|\|/g, '|')
        : undefined
}

export const extractElement = (
    elementData: Partial<TextContentElementInput>
) => {
    const { parentOptionGroupId, ...element } = elementData
    const text = element.text!
    const colonIndex = text?.indexOf(':')!
    const bracketIndex = text?.indexOf('(')!

    return element && element.type === 'OptionGroup'
        ? {
              ...element,
              elements: element?.elements?.map((childElementData) => {
                  const {
                      parentOptionGroupId,
                      ...childElement
                  } = childElementData
                  return childElement.type === 'SelectionOption'
                      ? {
                            ...childElement,
                            isListingActivatedOption:
                                childElement?.optionItemState === 'OptedIn'
                                    ? false
                                    : null,
                        }
                      : childElement
              }),
          }
        : bracketIndex !== -1 && colonIndex > bracketIndex
        ? {
              ...element,
              text: `${text.slice(0, bracketIndex - 1)} ${text.slice(
                  colonIndex
              )}`,
              placeholderText: `${text.slice(0, bracketIndex - 1)} ${text.slice(
                  colonIndex
              )}`,
          }
        : element
}

export const checkContentCauseKeyDuplicates = (
    input: CheckContentCauseKeyDuplicatesRequirements
) => {
    const { content, newParentElementID, articlesData } = input
    const newParentChildren = findInTemplates(
        articlesData,
        newParentElementID
    ) as NormalizedDocumentTemplateViewItem[]

    const propertyKey = extractKey(
        content as NormalizedDocumentTemplateViewItem,
        newParentElementID,
        articlesData
    )

    const newParentChildrenItems = newParentChildren?.map((item) => {
        return item?.contentItem
    }) as Partial<ContentItem>[]

    return checkKeyDuplicates(
        propertyKey as string,
        false,
        newParentChildrenItems
    )
}

export const isContentMovableToParent = (
    content: Partial<NormalizedDocumentTemplateViewItem> | undefined
) => {
    return !(!content || content?.isBOD || content?.isManufacturers)
}

export const isSustainabilityTopicContent = (
    content: NormalizedDocumentTemplateViewItem | undefined
) => {
    return (
        content?.partType === 'ContentGroup' ||
        content?.newProperties?.partType === 'ContentGroup'
    )
}

export const isSustainabilityTopicContentInNuggetorPTP = (
    property: NormalizedDocumentTemplateViewItem
) => {
    return property?.isSustainabilityContentItem
}

export const isContentAllowedToBeDemoted = (
    content: NormalizedDocumentTemplateViewItem | undefined,
    baseContentCanBeDemote: boolean
) => {
    const currentMinLevel = isSustainabilityTopicContent(content)
        ? 0
        : contentMinLevel
    return (
        content &&
        content.level > currentMinLevel && // Don't demote level 1 content for families and level 0 content for sustainability topic
        content.level < contentMaxLevel && // Don't demote last level
        content.order !== 0 && // Don't demote order 0
        (baseContentCanBeDemote ||
            (!baseContentCanBeDemote && content.isUserAdded === true)) && // Don't demote not user added content
        !content.isTableSibling // Don't demote paragraph under table
    )
}

export const isContentAllowedToBePromoted = (
    content: NormalizedDocumentTemplateViewItem | undefined,
    baseContentCanBePromote: boolean
) => {
    const currentMinLevel = isSustainabilityTopicContent(content)
        ? 0
        : contentMinLevel
    return (
        content &&
        content.level > currentMinLevel + 1 && // Don't promote level 1 and 2 content for families and for sustainability topic, its level 0 and 1
        (baseContentCanBePromote ||
            (!baseContentCanBePromote && content.isUserAdded === true)) // Don't promote not user added content
    )
}

export const removeDuplicateTemporaryProperty = (
    newChildren: NormalizedDocumentTemplateViewItem[]
) => {
    // find the first occurence of new temp property
    const firstTempProperty = newChildren?.findIndex(
        (x) => x?.id === defaultNewProperty.id
    )
    // if a new temp property is found, remove its duplicates if any
    if (firstTempProperty !== -1) {
        return newChildren?.filter((value, index) => {
            // get non-temporary templates and first occurence of temporary template only.
            return (
                value?.id !== defaultNewProperty.id ||
                index === firstTempProperty
            )
        })
    }

    // no temporary template was found, return original list
    return newChildren
}
/**
 * Converts an array of BulkAddElementInputWithMetadata to BulkAddElementInput by filtering out metadata properties
 * that are not needed for the API request.
 *
 * @param elements - Array of BulkAddElementInputWithMetadata objects containing both API data and metadata
 * @returns Array of BulkAddElementInput objects with metadata properties removed, ready for API consumption
 *
 * @example
 * ```typescript
 * const elementsWithMetadata = [
 *   { baseElementId: '1', projectId: 'proj1', isBasedFromManufacturerOption: true },
 *   { baseElementId: '2', projectId: 'proj1', isBasedFromManufacturerOption: false }
 * ];
 * const apiElements = convertToBulkAddElementInputs(elementsWithMetadata);
 * // Result: [{ baseElementId: '1', projectId: 'proj1' }, { baseElementId: '2', projectId: 'proj1' }]
 * ```
 */
export const convertToBulkAddElementInputs = (
    elements: BulkAddElementInputWithMetadata[]
): BulkAddElementInput[] => {
    return elements.map(
        ({ isBasedFromManufacturerOption, ...element }) => element
    )
}
